/**
 * CalendarGrid
 * Monthly calendar view with unified Events + Tasks
 */

import React, { useMemo } from 'react'
import { CalendarEventCard } from './CalendarEventCard'
import { CalendarTaskCard } from './CalendarTaskCard'
import type { CalendarItem } from '../../types/calendar'

interface CalendarGridProps {
  items: CalendarItem[]
  currentDate: Date
  onDateClick?: (date: Date) => void
  onItemClick?: (item: CalendarItem) => void
}

interface DayCell {
  date: Date
  isCurrentMonth: boolean
  isToday: boolean
  items: CalendarItem[]
}

/**
 * Generate calendar grid for the month
 */
const generateCalendarGrid = (year: number, month: number): DayCell[][] => {
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const startDate = new Date(firstDay)

  // Start from Sunday (0) or Monday (1) - adjust based on locale
  const firstDayOfWeek = 0 // Sunday
  const dayOfWeek = firstDay.getDay()
  const daysFromPrevMonth = (dayOfWeek - firstDayOfWeek + 7) % 7

  startDate.setDate(startDate.getDate() - daysFromPrevMonth)

  const weeks: DayCell[][] = []
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  for (let week = 0; week < 6; week++) {
    const days: DayCell[] = []

    for (let day = 0; day < 7; day++) {
      const currentDate = new Date(startDate)
      const isCurrentMonth = currentDate.getMonth() === month
      const isToday = currentDate.getTime() === today.getTime()

      days.push({
        date: new Date(currentDate),
        isCurrentMonth,
        isToday,
        items: []
      })

      startDate.setDate(startDate.getDate() + 1)
    }

    weeks.push(days)

    // Stop if we've passed the last day of the month
    if (startDate > lastDay && week >= 4) {
      break
    }
  }

  return weeks
}

/**
 * Assign items to calendar days
 */
const assignItemsToDays = (weeks: DayCell[][], items: CalendarItem[]): void => {
  items.forEach(item => {
    const itemDate = new Date(item.date)
    itemDate.setHours(0, 0, 0, 0)

    weeks.forEach(week => {
      week.forEach(day => {
        const dayDate = new Date(day.date)
        dayDate.setHours(0, 0, 0, 0)

        if (dayDate.getTime() === itemDate.getTime()) {
          day.items.push(item)
        }
      })
    })
  })

  // Sort items by time and type within each day
  weeks.forEach(week => {
    week.forEach(day => {
      day.items.sort((a, b) => {
        // Events with startTime come first, sorted by time
        if (a.type === 'event' && b.type === 'event') {
          if (a.startTime && b.startTime) {
            return a.startTime.localeCompare(b.startTime)
          }
          if (a.startTime) return -1
          if (b.startTime) return 1
        }

        // Events before tasks
        if (a.type === 'event' && b.type === 'task') return -1
        if (a.type === 'task' && b.type === 'event') return 1

        // Tasks by priority
        const priorityOrder = { URGENT: 0, HIGH: 1, MEDIUM: 2, LOW: 3 }
        const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder] ?? 4
        const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder] ?? 4
        return aPriority - bPriority
      })
    })
  })
}

/**
 * CalendarGrid Component
 */
export const CalendarGrid: React.FC<CalendarGridProps> = ({
  items,
  currentDate,
  onDateClick,
  onItemClick
}) => {
  const weekdays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

  const weeks = useMemo(() => {
    const grid = generateCalendarGrid(currentDate.getFullYear(), currentDate.getMonth())
    assignItemsToDays(grid, items)
    return grid
  }, [currentDate, items])

  return (
    <div className="min-w-0 overflow-x-auto rounded-lg border bg-card">
      <div className="min-w-[640px]">
      {/* Weekday headers */}
      <div className="grid grid-cols-7 border-b bg-muted/60">
        {weekdays.map(day => (
          <div
            key={day}
            className="py-2 text-center text-sm font-medium text-muted-foreground"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {weeks.map((week, weekIndex) =>
          week.map((day, dayIndex) => (
            <div
              key={`${weekIndex}-${dayIndex}`}
              onClick={() => onDateClick?.(day.date)}
              className={`
                min-h-[80px] min-w-0 sm:min-h-[120px] border-r border-b p-1 sm:p-2
                cursor-pointer hover:bg-muted transition-colors
                ${day.isToday ? 'bg-accent/60' : !day.isCurrentMonth ? 'bg-muted/40' : 'bg-background'}
              `}
            >
              {/* Day number */}
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`
                    text-sm font-medium
                    ${day.isToday
                      ? 'flex size-6 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground'
                      : !day.isCurrentMonth ? 'text-muted-foreground/60' : 'text-foreground'}
                  `}
                >
                  {day.date.getDate()}
                </span>
                {day.items.length > 0 && (
                  <span className="text-[10px] text-muted-foreground">
                    {day.items.length}
                  </span>
                )}
              </div>

              {/* Items */}
              <div className="space-y-1">
                {day.items.slice(0, 3).map((item) => (
                  <div
                    key={item.id}
                    onClick={(e) => {
                      e.stopPropagation()
                      onItemClick?.(item)
                    }}
                  >
                    {item.type === 'event' ? (
                      <CalendarEventCard item={item} className="text-xs" />
                    ) : (
                      <CalendarTaskCard item={item} className="text-xs" />
                    )}
                  </div>
                ))}

                {/* Show +N more indicator */}
                {day.items.length > 3 && (
                  <div className="text-[10px] text-muted-foreground font-medium pl-2">
                    +{day.items.length - 3} mais
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      </div>
    </div>
  )
}
